// 업무 통합 1단계 · 고객사 기준 정보 및 이력관리(개발요청 1-운영-10) — 위: 목록(왼쪽)·상세(오른쪽) · 아래: 기준 변경이력 · 이력 정리.
// API: api/client workCustomersAPI(/api/work-customers …) · 메뉴 work-customers(보기) · work-customers-edit(본사 편집).
// 🔴 권한은 서버가 정본 — 화면은 응답의 can_edit·can_center_edit·editable_fields 로 단추만 나눈다(서버가 이미 403/404).
// 🔴 포장지시서·견적서·엑셀 가져오기 화면은 다음 조각(P4) — 이 화면은 그 단추를 그리지 않는다.
// 목록은 상태(운영·종료·전체)만 서버가 거르고 검색·센터·담당·빠른 필터는 화면이 거른다(filterCustomers).
// 하위 카드가 무언가 바꾸면 onChanged → 목록·상세·변경이력·수기 이력을 다시 읽는다(reloadKey).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconAddressBook } from '@tabler/icons-react';
import AppShell from '../components/AppShell';
import { Button, Page, PageHeader } from '../components/ui';
import { workCustomersAPI } from '../api/client';
import { COLORS } from '../utils/uiStyles';
import CustomerListCard from './workCustomers/CustomerListCard';
import CustomerDetailCard from './workCustomers/CustomerDetailCard';
import HistoryCard from './workCustomers/HistoryCard';
import LogsCard from './workCustomers/LogsCard';
import { errorText, filterCustomers, statusOf } from './workCustomers/workCustomerLabels';

export const PAGE_TITLE = '고객사 기준 정보 및 이력관리';
export const PAGE_SUBTITLE = '고객사별 운영 기준, 포장지시서, 견적서와 변경·인수인계·수기 이력을 관리합니다.';
const EMPTY_FILTERS = { q: '', center: '', ops: '', sales: '', quick: [] };
// 넓은 화면 2열(목록 · 상세) · 1100px 미만 1열 — 인라인 스타일로는 미디어 쿼리를 못 써서 클래스 하나만 둔다.
const GRID_CSS = '.gw-wc-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:16px;align-items:start}'
  + '@media (max-width:1099px){.gw-wc-grid{grid-template-columns:minmax(0,1fr)}}';

export default function WorkCustomersPage() {
  const [status, setStatus] = useState('운영');
  const [list, setList] = useState(null);
  const [listState, setListState] = useState('loading');   // loading | ready | forbidden | error
  const [listError, setListError] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const loadList = useCallback(async (wanted) => {
    try {
      const res = await workCustomersAPI.list({ status: wanted });
      setList(res && res.data ? res.data : { items: [] });
      setListState('ready');
      return true;
    } catch (e) {
      setList(null);
      setListState(statusOf(e) === 403 ? 'forbidden' : 'error');
      setListError(errorText(e, '고객사 목록을 불러오지 못했습니다'));
      return false;
    }
  }, []);

  useEffect(() => {
    setListState('loading');
    loadList(status);
  }, [status, loadList]);

  const items = useMemo(() => (list && Array.isArray(list.items) ? list.items : []), [list]);
  const visible = useMemo(() => filterCustomers(items, filters), [items, filters]);

  // 첫 진입·필터·상태 뒤 선택이 목록에 없으면 첫 행(목록이 비면 선택 없음).
  useEffect(() => {
    if (listState !== 'ready') return;
    if (!visible.some((c) => c.id === selectedId)) setSelectedId(visible.length ? visible[0].id : null);
  }, [visible, selectedId, listState]);

  useEffect(() => {
    if (selectedId == null) { setDetail(null); setDetailError(''); return undefined; }
    let alive = true;
    setDetailError('');
    workCustomersAPI.get(selectedId)
      .then((res) => { if (alive) setDetail(res && res.data ? res.data : null); })
      .catch((e) => {
        if (!alive) return;
        setDetail(null);
        setDetailError(statusOf(e) === 404 ? '고객사를 찾을 수 없습니다' : errorText(e, '고객사 상세를 불러오지 못했습니다'));
      });
    return () => { alive = false; };
  }, [selectedId, reloadKey]);

  const onChanged = useCallback(() => {
    loadList(status);
    setReloadKey((k) => k + 1);
  }, [loadList, status]);

  const onCreated = useCallback(async (id) => {
    await loadList(status);
    if (id != null) setSelectedId(id);
    setReloadKey((k) => k + 1);
  }, [loadList, status]);

  const shownDetail = detail && detail.customer && detail.customer.id === selectedId ? detail : null;
  const selectedRow = items.find((c) => c.id === selectedId) || null;
  const customerName = shownDetail ? shownDetail.customer.name : (selectedRow ? selectedRow.name : '');
  const canWriteLogs = !!(shownDetail && (shownDetail.can_center_edit || shownDetail.can_edit));

  return (
    <AppShell app="work-customers">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', display: 'flex', overflow: 'hidden', background: `var(--gw-bg, ${COLORS.bg})` }}>
        <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
          <Page>
            <style>{GRID_CSS}</style>
            <PageHeader icon={IconAddressBook} tone="blue" title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
            {listState === 'forbidden' && (
              <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px' }}>이 화면을 볼 권한이 없습니다.</div>
            )}
            {listState === 'error' && (
              <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                {listError}
                <Button variant="secondary" size="sm" onClick={() => { setListState('loading'); loadList(status); }}>다시 시도</Button>
              </div>
            )}
            {listState !== 'forbidden' && listState !== 'error' && (
              <>
                <div className="gw-wc-grid">
                  <CustomerListCard
                    items={visible}
                    loading={listState === 'loading'}
                    status={status}
                    onStatus={setStatus}
                    filters={filters}
                    onFilters={(patch) => setFilters((f) => ({ ...f, ...patch }))}
                    options={list || {}}
                    canEdit={!!(list && list.can_edit)}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    onCreated={onCreated}
                  />
                  <CustomerDetailCard customerId={selectedId} detail={shownDetail} error={detailError} onChanged={onChanged} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                  <HistoryCard customerId={selectedId} customerName={customerName} reloadKey={reloadKey} />
                  <LogsCard
                    customerId={selectedId}
                    customerName={customerName}
                    canWrite={canWriteLogs}
                    logTypes={shownDetail ? shownDetail.log_types : null}
                    reloadKey={reloadKey}
                  />
                </div>
              </>
            )}
          </Page>
        </div>
      </main>
    </AppShell>
  );
}
